import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { contractsAPI, vehiclesAPI, driversAPI, maintenanceAPI, vehicleTypesAPI, availabilityAPI, operationalLinesAPI } from "../lib/api";
import { formatVehicleLabel, isPostServiceByDate } from "../lib/formatters";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  MapPin,
  Clock,
  Users,
  Truck,
  User,
  Circle,
  ArrowRight,
  AlertTriangle,
  Ban,
  CheckCircle,
  Bus,
  Filter
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";

export default function Asignacion() {
  const { canManageAssignments } = useAuth();
  const readOnly = !canManageAssignments;
  const [operationalLines, setOperationalLines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // v4.56.0: listas completas (incluye inactivas) para resolver históricos sin afectar selectores de nueva asignación
  const [vehiclesAll, setVehiclesAll] = useState([]);
  const [driversAll, setDriversAll] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vehicleAvailability, setVehicleAvailability] = useState({});
  const [driverConflicts, setDriverConflicts] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [assignmentData, setAssignmentData] = useState({
    vehicle_id: "",
    driver_id: "",
    traffic_light: "amarillo"
  });
  const [filterType, setFilterType] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [includePast, setIncludePast] = useState(false);
  const [postServiceReason, setPostServiceReason] = useState("");

  const trafficLightColors = {
    rojo: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", dot: "bg-red-500" },
    amarillo: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300", dot: "bg-yellow-500" },
    verde: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300", dot: "bg-green-500" }
  };
  
  // Helper: Get vehicle type label from catalog (dynamic)
  // DOES NOT infer/guess for ambiguous legacy codes - flags them for review
  const getVehicleTypeLabel = (typeCode) => {
    if (!typeCode) return "Sin tipo";
    
    // 1. Try exact match in the loaded vehicle types catalog
    const exactMatch = vehicleTypes.find(vt => vt.code === typeCode);
    if (exactMatch) {
      return exactMatch.name; // e.g., "Autobús 53 Pax", "Sprinter 20 Pax"
    }
    
    // 2. Check if this is an ambiguous legacy code (generic code that has multiple
    //    specific variants in the catalog, e.g., "autobus" when catalog has autobus_45, autobus_53, etc.)
    const possibleMatches = vehicleTypes.filter(vt => 
      vt.code.startsWith(typeCode + "_") || 
      vt.code.startsWith(typeCode.toLowerCase() + "_")
    );
    
    if (possibleMatches.length > 0) {
      // Ambiguous legacy code - DO NOT guess, flag for review
      const baseName = typeCode.charAt(0).toUpperCase() + typeCode.slice(1).toLowerCase();
      return `${baseName} (revisar capacidad)`;
    }
    
    // 3. Simple fallback labels for truly legacy codes (no ambiguity)
    const fallbackLabels = {
      sedan: "Sedán",
      suv: "SUV",
      van: "Van",
      sprinter: "Sprinter",
      suburban: "Suburban",
      autobus: "Autobús (revisar capacidad)",
      bus: "Autobús (revisar capacidad)",
      limusina: "Limusina"
    };
    
    return fallbackLabels[typeCode.toLowerCase()] || typeCode;
  };
  
  // v4.59.x FIX P0: Construir tarjetas desde la capa operativa (operational_lines).
  // Cada tarjeta usa la VENTANA EFECTIVA de la línea (jerarquía v2 > line_* > global),
  // NO la fecha global del contrato. Misma fuente conceptual que Calendario/ContratoDetalle.
  const buildUnitsFromOperationalLines = (lines) => {
    const totals = {};
    lines.forEach(ol => { totals[ol.contract_id] = (totals[ol.contract_id] || 0) + 1; });
    const seen = {};
    return lines.map((ol, idx) => {
      seen[ol.contract_id] = (seen[ol.contract_id] || 0) + 1;
      const hasV = !!ol.assigned_vehicle_id;
      const hasD = !!ol.assigned_driver_id;
      const traffic_light = hasV && hasD ? "verde" : (hasV || hasD) ? "amarillo" : "rojo";
      return {
        unit_id: ol.vehicle_line_id || `${ol.contract_id}-${idx}`,
        unit_index: seen[ol.contract_id],
        total_units: totals[ol.contract_id] || 1,
        vehicle_line_id: ol.vehicle_line_id || null,
        assignment_id: ol.assignment_id || null,
        assignment_status: ol.assignment_status || null,
        window_source: ol.window_source || "global",
        origin_source: ol.origin_source || "global",
        destination_source: ol.destination_source || "global",
        unit_label: ol.unit_label || "",
        vehicle_type: ol.vehicle_type || "sedan",
        vehicle_capacity: ol.capacity || 0,
        contract_id: ol.contract_id,
        contract_folio: ol.folio || `CT-${(ol.contract_id || "").slice(0, 8)}`,
        contract_status: ol.contract_status,
        client_name: ol.client_name,
        service_type: ol.service_type,
        // Ventana efectiva (alias pickup/end para compatibilidad con chequeos existentes)
        origin: ol.effective_origin,
        destination: ol.effective_destination,
        pickup_datetime: ol.effective_start,
        end_datetime: ol.effective_end,
        effective_start: ol.effective_start,
        effective_end: ol.effective_end,
        effective_origin: ol.effective_origin,
        effective_destination: ol.effective_destination,
        // v4.59.6: ventana GLOBAL del contrato (read-only) — SOLO para detección post-servicio
        // (alineada con backend is_post_service). NO usar para disponibilidad/asignación.
        contract_pickup_datetime: ol.contract_pickup_datetime,
        contract_end_datetime: ol.contract_end_datetime,
        vehicle_id: ol.assigned_vehicle_id || null,
        driver_id: ol.assigned_driver_id || null,
        assigned_vehicle_name: ol.assigned_vehicle_name || null,
        assigned_driver_name: ol.assigned_driver_name || null,
        partial_assignment: ol.partial_assignment || null,
        traffic_light,
        is_assigned: !!ol.is_assigned,
        is_preassignment: !!ol.is_preassignment,
        requires_subcontract: !!ol.requires_subcontract,
        window_is_custom: !!ol.window_is_custom,
      };
    });
  };

  // Tarjetas derivadas de la capa operativa
  const assignmentUnits = buildUnitsFromOperationalLines(operationalLines);
  
  // Apply filters
  const activeFilterType = filterType && filterType !== "all" ? filterType : "";
  const activeFilterMonth = filterMonth && filterMonth !== "all" ? filterMonth : "";
  
  const filteredUnits = assignmentUnits.filter(unit => {
    if (activeFilterType && unit.vehicle_type !== activeFilterType) return false;
    if (activeFilterMonth && unit.pickup_datetime) {
      const unitMonth = unit.pickup_datetime.substring(0, 7);
      if (unitMonth !== activeFilterMonth) return false;
    }
    if (activeFilterMonth && !unit.pickup_datetime) return false;
    return true;
  });
  
  // Available months for month filter dropdown.
  // v4.64.0: siempre incluir el mes actual + 12 meses futuros (tomando la fecha actual como
  // referencia), unido con los meses presentes en los datos (para no perder meses previos
  // relevantes). Antes solo se derivaba de los datos → el dropdown se quedaba corto.
  const availableMonths = (() => {
    const set = new Set(
      assignmentUnits.filter(u => u.pickup_datetime).map(u => u.pickup_datetime.substring(0, 7))
    );
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return [...set].sort();
  })();
  
  // Available types from data (for type filter dropdown)
  const availableTypes = [...new Set(
    assignmentUnits.map(u => u.vehicle_type).filter(Boolean)
  )].sort();
  
  // Group by traffic light for display (from filtered set)
  const redUnits = filteredUnits.filter(u => u.traffic_light === "rojo");
  const yellowUnits = filteredUnits.filter(u => u.traffic_light === "amarillo");
  const greenUnits = filteredUnits.filter(u => u.traffic_light === "verde");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includePast]);

  const fetchData = async () => {
    try {
      const [contractsRes, vehiclesRes, driversRes, vehicleTypesRes] = await Promise.all([
        contractsAPI.getAll(),  // Fetch ALL contracts (para acotar la ventana y filtrar por estado)
        vehiclesAPI.getAll(),
        driversAPI.getAll(),
        vehicleTypesAPI.getAll()
      ]);

      // Filtrar: excluir cancelados siempre; excluir completados salvo toggle
      const allowed = (contractsRes.data || []).filter(c => {
        if (c.status === "cancelado") return false;
        if (!includePast && c.status === "completado") return false;
        return true;
      });
      const allowedIds = new Set(allowed.map(c => c.id));

      // Calcular ventana [start, end] a partir de los contratos permitidos (acota el batch)
      let minS = null, maxE = null;
      allowed.forEach(c => {
        if (c.pickup_datetime && (!minS || c.pickup_datetime < minS)) minS = c.pickup_datetime;
        const e = c.end_datetime || c.pickup_datetime;
        if (e && (!maxE || e > maxE)) maxE = e;
      });
      const norm = (v, fallback, tail) => {
        const base = v || fallback;
        return base.includes("T") ? base.slice(0, 16) : `${base}${tail}`;
      };
      const start = norm(minS, "2000-01-01", "T00:00");
      const end = norm(maxE, "2100-12-31", "T23:59");

      // Cargar la capa operativa (1 pieza por línea, ventana efectiva)
      let lines = [];
      if (allowed.length > 0) {
        try {
          const olRes = await operationalLinesAPI.list({ start, end, expand_quantity: false });
          lines = (olRes.data?.operational_lines || []).filter(ol => allowedIds.has(ol.contract_id));
        } catch (err) {
          toast.error(err.response?.data?.detail || "Error al cargar líneas operativas");
        }
      }
      setOperationalLines(lines);

      // v4.56.0: listas completas para resolver históricos; selectores usan solo activos
      setVehiclesAll(vehiclesRes.data || []);
      setDriversAll(driversRes.data || []);
      setVehicles((vehiclesRes.data || []).filter(v => v.status === "activo"));
      setDrivers((driversRes.data || []).filter(d => d.status === "activo"));
      setVehicleTypes(vehicleTypesRes.data || []);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const checkVehicleAvailability = async (contract) => {
    if (!contract.pickup_datetime) return;
    
    setLoadingAvailability(true);
    try {
      // Send full datetimes for accurate hourly availability check
      const startDt = contract.pickup_datetime;
      const endDt = contract.end_datetime || contract.pickup_datetime;
      const response = await maintenanceAPI.checkAvailability({
        start_date: startDt,
        end_date: endDt,
        exclude_contract_id: contract.id
      });
      
      // Convert array response to object keyed by unit_id
      const availabilityMap = {};
      response.data.forEach(unit => {
        availabilityMap[unit.unit_id] = unit;
      });
      setVehicleAvailability(availabilityMap);
    } catch (error) {
      console.error("Error checking availability:", error);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleAssign = async (contract) => {
    setSelectedContract(contract);
    setAssignmentData({
      vehicle_id: contract.vehicle_id || "",
      driver_id: contract.driver_id || "",
      traffic_light: contract.traffic_light || "amarillo"
    });
    setDialogOpen(true);
    
    // Check availability for all vehicles on this date
    await checkVehicleAvailability(contract);
  };
  
  // NEW: Handle assignment for individual unit
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  const handleAssignUnit = async (unit) => {
    // Read-only roles cannot open the assignment dialog
    if (readOnly) return;

    // Objeto tipo-contrato para el diálogo, usando la VENTANA EFECTIVA de la línea.
    // pickup_datetime / end_datetime quedan como alias de la ventana efectiva para que
    // los chequeos de disponibilidad y mantenimiento operen sobre la línea (no el global).
    const contractData = {
      id: unit.contract_id,
      unit_id: unit.unit_id,
      unit_index: unit.unit_index,
      total_units: unit.total_units,
      unit_label: unit.unit_label,
      vehicle_line_id: unit.vehicle_line_id,
      assignment_id: unit.assignment_id,
      assignment_status: unit.assignment_status,
      window_source: unit.window_source,
      origin_source: unit.origin_source,
      destination_source: unit.destination_source,
      vehicle_type: unit.vehicle_type,
      vehicle_capacity: unit.vehicle_capacity,
      client_name: unit.client_name,
      service_type: unit.service_type,
      origin: unit.effective_origin,
      destination: unit.effective_destination,
      pickup_datetime: unit.effective_start,
      end_datetime: unit.effective_end,
      effective_start: unit.effective_start,
      effective_end: unit.effective_end,
      // v4.59.6: ventana GLOBAL (read-only) para detección post-servicio alineada al backend
      contract_pickup_datetime: unit.contract_pickup_datetime,
      contract_end_datetime: unit.contract_end_datetime,
      traffic_light: unit.traffic_light,
      vehicle_id: unit.vehicle_id,
      driver_id: unit.driver_id,
      folio: unit.contract_folio
    };

    setSelectedUnit(unit);
    setSelectedContract(contractData);
    setAssignmentData({
      vehicle_id: unit.vehicle_id || "",
      driver_id: unit.driver_id || "",
      traffic_light: unit.traffic_light || "amarillo"
    });
    setPostServiceReason("");
    setDialogOpen(true);
    
    // Chequeo de disponibilidad/mantenimiento sobre la ventana EFECTIVA de la línea
    await checkVehicleAvailability(contractData);
    
    // Correct false positives: re-check pre-assigned resources WITH exclude_contract_id
    const [vResult, dResult] = await Promise.all([
      unit.vehicle_id ? checkResourceConflict("vehicle", unit.vehicle_id, contractData) : Promise.resolve(null),
      unit.driver_id ? checkResourceConflict("driver", unit.driver_id, contractData) : Promise.resolve(null)
    ]);
    if (vResult) {
      setVehicleAvailability(prev => ({ ...prev, [unit.vehicle_id]: vResult }));
    }
    if (dResult) {
      setDriverConflicts(prev => ({ ...prev, [unit.driver_id]: dResult }));
    }
  };

  // Detect if selected contract is post-service (end_datetime < now)
  // v4.59.6: usa la ventana GLOBAL del contrato (contract_end_datetime), NO la efectiva,
  // porque el backend (is_post_service) decide con base en el end_datetime GLOBAL.
  // isPostServiceByDate (v4.59.5) interpreta fechas naive como UTC para alinear con el backend.
  const isSelectedPostService = (() => {
    const c = selectedUnit || selectedContract;
    if (!c) return false;
    const ref = c.contract_end_datetime || c.contract_pickup_datetime;
    return isPostServiceByDate(ref);
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!assignmentData.vehicle_id || !assignmentData.driver_id) {
      toast.error("Selecciona unidad y chofer");
      return;
    }

    if (isSelectedPostService && (!postServiceReason || postServiceReason.trim().length < 10)) {
      toast.error("Modificación post-servicio requiere motivo (mínimo 10 caracteres)");
      return;
    }

    const unit = selectedUnit;
    try {
      // v4.59.x: guardar vía capa de ejecución v2 (POST/PUT assignments) usando la VENTANA EFECTIVA.
      // origin/destination solo se envían si la línea los tiene personalizados; si no, null = hereda.
      const isCustomOrigin = unit?.origin_source === "line";
      const isCustomDest = unit?.destination_source === "line";
      const basePayload = {
        vehicle_id: assignmentData.vehicle_id,
        driver_id: assignmentData.driver_id,
        start_datetime: unit?.effective_start || selectedContract?.pickup_datetime,
        end_datetime: unit?.effective_end || selectedContract?.end_datetime,
        vehicle_line_id: unit?.vehicle_line_id || null,
        origin: isCustomOrigin ? (unit?.effective_origin || null) : null,
        destination: isCustomDest ? (unit?.effective_destination || null) : null,
        notes: null
      };

      // ¿Editar una asignación v2 existente o crear/promover una nueva?
      const isV2 = !!(unit?.assignment_id && unit?.assignment_status === "activa");

      if (isV2) {
        const editPayload = isSelectedPostService
          ? { vehicle_id: assignmentData.vehicle_id, driver_id: assignmentData.driver_id, notes: null, post_service_change_reason: postServiceReason.trim() }
          : basePayload;
        await contractsAPI.updateAssignment2(selectedContract.id, unit.assignment_id, editPayload);
      } else {
        const createPayload = isSelectedPostService
          ? { ...basePayload, post_service_change_reason: postServiceReason.trim() }
          : basePayload;
        await contractsAPI.createAssignment(selectedContract.id, createPayload);
      }

      // Persistir el semáforo manual a nivel contrato (contract.traffic_light).
      // No envía vehicle/driver/vl_index → no revalida disponibilidad (sin reintroducir ventana global).
      // El modelo v2 preserva "verde" como confirmación manual (update_contract_traffic_light).
      if (assignmentData.traffic_light) {
        try {
          const tlPayload = isSelectedPostService
            ? { traffic_light: assignmentData.traffic_light, post_service_change_reason: postServiceReason.trim() }
            : { traffic_light: assignmentData.traffic_light };
          await contractsAPI.updateAssignment(selectedContract.id, tlPayload);
        } catch (tlErr) {
          // No bloquear el flujo principal si solo falla la persistencia del semáforo
          console.warn("[Asignacion] No se pudo persistir el semáforo manual:", tlErr.response?.data?.detail);
        }
      }

      toast.success(isSelectedPostService ? "Asignación post-servicio actualizada (OS regenerada)" : "Asignación actualizada");
      setDialogOpen(false);
      setPostServiceReason("");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Error al actualizar";
      toast.error(detail);
    }
  };

  // Check resource conflicts when vehicle or driver selection changes
  // contractOverride allows passing contract data directly (avoids stale state on dialog open)
  const checkResourceConflict = async (resourceType, resourceId, contractOverride = null) => {
    const contract = contractOverride || selectedContract;
    if (!resourceId || !contract) return null;
    try {
      const res = await availabilityAPI.check({
        resource_type: resourceType,
        resource_id: resourceId,
        start_datetime: contract.pickup_datetime || "",
        end_datetime: contract.end_datetime || contract.pickup_datetime || "",
        exclude_contract_id: contract.id
      });
      return res.data;
    } catch { return null; }
  };

  const handleVehicleChange = async (vehicleId) => {
    setAssignmentData(prev => ({ ...prev, vehicle_id: vehicleId }));
    if (vehicleId) {
      const result = await checkResourceConflict("vehicle", vehicleId);
      if (result) {
        setVehicleAvailability(prev => ({ ...prev, [vehicleId]: result }));
      }
    }
  };

  const handleDriverChange = async (driverId) => {
    setAssignmentData(prev => ({ ...prev, driver_id: driverId }));
    if (driverId) {
      const result = await checkResourceConflict("driver", driverId);
      if (result) {
        setDriverConflicts(prev => ({ ...prev, [driverId]: result }));
      }
    }
  };

  // groupedContracts removed - now using assignmentUnits (redUnits, yellowUnits, greenUnits)

  // v4.56.0: lookups históricos resuelven contra la lista COMPLETA (incluye inactivos)
  const getVehicle = (id) => vehiclesAll.find(v => v.id === id);
  const getDriver = (id) => driversAll.find(d => d.id === id);
  // Para selectores de nueva asignación: solo activos + currentId si está ligado a un inactivo (edición histórica)
  const getVehiclesForForm = (currentId = null) => {
    if (currentId && !vehicles.find(v => v.id === currentId)) {
      const historical = vehiclesAll.find(v => v.id === currentId);
      if (historical) return [...vehicles, historical];
    }
    return vehicles;
  };
  const getDriversForForm = (currentId = null) => {
    if (currentId && !drivers.find(d => d.id === currentId)) {
      const historical = driversAll.find(d => d.id === currentId);
      if (historical) return [...drivers, historical];
    }
    return drivers;
  };
  const vehicleSelectLabel = (v) => {
    const base = formatVehicleLabel(v);
    return v?.status === "inactivo" ? `${base} (inactiva)` : base;
  };
  const driverSelectLabel = (d) => {
    const base = `${d?.name || ""} - ${d?.phone || ""}`;
    return d?.status === "inactivo" ? `${base} (inactivo)` : base;
  };

  const isVehicleBlocked = (vehicleId) => {
    const availability = vehicleAvailability[vehicleId];
    return availability && !availability.available;
  };

  const isDriverBlocked = (driverId) => {
    const conflict = driverConflicts[driverId];
    return conflict && !conflict.available;
  };

  return (
    <Layout>
      <TooltipProvider>
        <div className="p-6 lg:p-8 space-y-6" data-testid="asignacion-page">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Panel de Asignación</h1>
            <p className="text-slate-500 mt-1">Asigna unidades y choferes a los servicios</p>
          </div>

          {readOnly && (
            <div
              className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800"
              data-testid="asignacion-readonly-banner"
            >
              <AlertTriangle className="w-4 h-4" />
              <span>Modo solo lectura — Coordina con Logística para modificar asignaciones.</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Unidades Sin Asignar</p>
                    <p className="text-3xl font-bold text-red-600">{redUnits.length}</p>
                  </div>
                  <Circle className="w-8 h-8 text-red-500 fill-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Asignación Inicial</p>
                    <p className="text-3xl font-bold text-yellow-600">{yellowUnits.length}</p>
                  </div>
                  <Circle className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Confirmadas</p>
                    <p className="text-3xl font-bold text-green-600">{greenUnits.length}</p>
                  </div>
                  <Circle className="w-8 h-8 text-green-500 fill-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters Bar */}
          <div className="flex flex-wrap items-center gap-3 p-4 bg-slate-50 rounded-lg border" data-testid="assignment-filters">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Filtrar:</span>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48 bg-white" data-testid="filter-vehicle-type">
                <SelectValue placeholder="Tipo de unidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {availableTypes.map(typeCode => (
                  <SelectItem key={typeCode} value={typeCode}>
                    {getVehicleTypeLabel(typeCode)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="w-48 bg-white" data-testid="filter-month">
                <SelectValue placeholder="Mes del servicio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {availableMonths.map(m => {
                  const [y, mo] = m.split("-");
                  const label = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
                  return <SelectItem key={m} value={m}>{label.charAt(0).toUpperCase() + label.slice(1)}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            {(activeFilterType || activeFilterMonth) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilterType(""); setFilterMonth(""); }}
                className="text-slate-500 hover:text-slate-700"
                data-testid="clear-filters-btn"
              >
                Limpiar filtros
              </Button>
            )}
            {!readOnly && (
              <label className="flex items-center gap-2 ml-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="include-past-toggle-label">
                <input
                  type="checkbox"
                  checked={includePast}
                  onChange={(e) => setIncludePast(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="include-past-toggle"
                />
                Incluir servicios completados
              </label>
            )}
            {(activeFilterType || activeFilterMonth) && (
              <Badge variant="secondary" className="ml-auto">
                {filteredUnits.length} de {assignmentUnits.length} tarjetas
              </Badge>
            )}
          </div>

          {/* Units Grid - ONE card per UNIT */}
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Red Column - Sin Asignar */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Circle className="w-4 h-4 text-red-500 fill-red-500" />
                  <h2 className="font-heading font-semibold text-slate-900">Sin Asignar</h2>
                </div>
                {redUnits.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-6 text-center text-slate-400">
                      No hay unidades sin asignar
                    </CardContent>
                  </Card>
                ) : (
                  redUnits.map((unit, uidx) => (
                    <Card 
                      key={`${unit.unit_id}-${uidx}`} 
                      className={cn("card-hover", trafficLightColors.rojo.border, !readOnly && "cursor-pointer")}
                      onClick={readOnly ? undefined : () => handleAssignUnit(unit)}
                      data-testid={`unit-card-${unit.unit_id}`}
                    >
                      <CardContent className="p-4 space-y-3">
                        {/* Vehicle type first */}
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-2">
                            <Bus className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-blue-700">{getVehicleTypeLabel(unit.vehicle_type)}</span>
                          </div>
                          {unit.total_units > 1 && (
                            <Badge variant="secondary" className="text-xs">
                              {unit.unit_index}/{unit.total_units}
                            </Badge>
                          )}
                        </div>
                        {/* Client name */}
                        <p className="font-medium text-slate-700">{unit.client_name}</p>
                        {/* Service type */}
                        <Badge variant="outline" className="text-xs">{unit.service_type}</Badge>
                        {/* v4.61.0: sin unidad interna disponible → requiere subarrendamiento */}
                        {unit.requires_subcontract && (
                          <div className="rounded-md bg-orange-50 border border-orange-300 px-2 py-1" data-testid={`subcontract-badge-${unit.unit_id}`}>
                            <p className="text-[11px] font-semibold text-orange-800">⚠️ Requiere subarrendamiento</p>
                            <p className="text-[10px] text-orange-700">Sin unidad interna disponible con este tipo/capacidad</p>
                          </div>
                        )}
                        <div className="text-sm text-slate-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span className="truncate">{unit.origin}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <span className="truncate">{unit.destination}</span>
                          </div>
                        </div>
                        {/* Date/Time (ventana efectiva de la línea) */}
                        <div className="flex items-center gap-1 text-sm text-slate-500" data-testid={`unit-date-${unit.unit_id}`}>
                          <Clock className="w-4 h-4" />
                          {unit.pickup_datetime
                            ? new Date(unit.pickup_datetime).toLocaleDateString("es-MX", {
                                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
                              })
                            : "—"}
                          {unit.window_is_custom ? (
                            <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 border-orange-400 text-orange-700" data-testid={`unit-window-source-${unit.unit_id}`}>
                              Ventana personalizada
                            </Badge>
                          ) : (unit.window_source === "line" && (
                            <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 border-amber-400 text-amber-700" data-testid={`unit-window-source-${unit.unit_id}`}>
                              Personalizada
                            </Badge>
                          ))}
                        </div>
                        {/* Folio */}
                        <div className="text-xs text-slate-400 pt-1 border-t">
                          Contrato: {unit.contract_folio}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Yellow Column - Asignación Inicial */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Circle className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <h2 className="font-heading font-semibold text-slate-900">Asignación Inicial</h2>
                </div>
                {yellowUnits.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-6 text-center text-slate-400">
                      No hay unidades en asignación inicial
                    </CardContent>
                  </Card>
                ) : (
                  yellowUnits.map((unit, uidx) => {
                    const vehicle = getVehicle(unit.vehicle_id);
                    const driver = getDriver(unit.driver_id);
                    return (
                      <Card 
                        key={`${unit.unit_id}-${uidx}`} 
                        className={cn("card-hover", trafficLightColors.amarillo.border, !readOnly && "cursor-pointer")}
                        onClick={readOnly ? undefined : () => handleAssignUnit(unit)}
                        data-testid={`unit-card-${unit.unit_id}`}
                      >
                        <CardContent className="p-4 space-y-3">
                          {/* Vehicle type first */}
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <Bus className="w-5 h-5 text-blue-600" />
                              <span className="font-semibold text-blue-700">{getVehicleTypeLabel(unit.vehicle_type)}</span>
                            </div>
                            {unit.total_units > 1 && (
                              <Badge variant="secondary" className="text-xs">
                                {unit.unit_index}/{unit.total_units}
                              </Badge>
                            )}
                          </div>
                          {/* Client */}
                          <p className="font-medium text-slate-700">{unit.client_name}</p>
                          {/* Service type */}
                          <Badge variant="outline" className="text-xs">{unit.service_type}</Badge>
                          {/* v4.61.0: Pre-asignación automática (pendiente de confirmación logística) */}
                          {unit.is_preassignment && (
                            <div className="rounded-md bg-yellow-50 border border-yellow-300 px-2 py-1" data-testid={`preassigned-badge-${unit.unit_id}`}>
                              <p className="text-[11px] font-semibold text-yellow-800">🟡 Pre-asignada automáticamente</p>
                              <p className="text-[10px] text-yellow-700">Pendiente de confirmación logística</p>
                            </div>
                          )}
                          {/* Date/Time (ventana efectiva de la línea) */}
                          <div className="flex items-center gap-1 text-sm text-slate-500" data-testid={`unit-date-${unit.unit_id}`}>
                            <Clock className="w-4 h-4" />
                            {unit.pickup_datetime
                              ? new Date(unit.pickup_datetime).toLocaleDateString("es-MX", {
                                  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
                                })
                              : "—"}
                            {unit.window_is_custom ? (
                              <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 border-orange-400 text-orange-700" data-testid={`unit-window-source-${unit.unit_id}`}>
                                Ventana personalizada
                              </Badge>
                            ) : (unit.window_source === "line" && (
                              <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 border-amber-400 text-amber-700" data-testid={`unit-window-source-${unit.unit_id}`}>
                                Personalizada
                              </Badge>
                            ))}
                          </div>
                          {/* Assignment info */}
                          <div className="pt-2 border-t space-y-1 text-sm">
                            {vehicle && (
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-slate-400" />
                                <span>{formatVehicleLabel(vehicle)}</span>
                              </div>
                            )}
                            {driver && (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span>{driver.name}</span>
                              </div>
                            )}
                          </div>
                          {/* Folio */}
                          <div className="text-xs text-slate-400">
                            Contrato: {unit.contract_folio}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>

              {/* Green Column - Confirmados */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Circle className="w-4 h-4 text-green-500 fill-green-500" />
                  <h2 className="font-heading font-semibold text-slate-900">Confirmadas</h2>
                </div>
                {greenUnits.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-6 text-center text-slate-400">
                      No hay unidades confirmadas
                    </CardContent>
                  </Card>
                ) : (
                  greenUnits.map((unit, uidx) => {
                    const vehicle = getVehicle(unit.vehicle_id);
                    const driver = getDriver(unit.driver_id);
                    return (
                      <Card 
                        key={`${unit.unit_id}-${uidx}`} 
                        className={cn("card-hover", trafficLightColors.verde.border, !readOnly && "cursor-pointer")}
                        onClick={readOnly ? undefined : () => handleAssignUnit(unit)}
                        data-testid={`unit-card-${unit.unit_id}`}
                      >
                        <CardContent className="p-4 space-y-3">
                          {/* Vehicle type first */}
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <Bus className="w-5 h-5 text-green-600" />
                              <span className="font-semibold text-green-700">{getVehicleTypeLabel(unit.vehicle_type)}</span>
                            </div>
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              Confirmada
                            </Badge>
                          </div>
                          {/* Unit badge if multi-unit */}
                          {unit.total_units > 1 && (
                            <Badge variant="secondary" className="text-xs">
                              Unidad {unit.unit_index}/{unit.total_units}
                            </Badge>
                          )}
                          {/* Client */}
                          <p className="font-medium text-slate-700">{unit.client_name}</p>
                          {/* Date/Time (ventana efectiva de la línea) */}
                          <div className="flex items-center gap-1 text-sm text-slate-500" data-testid={`unit-date-${unit.unit_id}`}>
                            <Clock className="w-4 h-4" />
                            {unit.pickup_datetime
                              ? new Date(unit.pickup_datetime).toLocaleDateString("es-MX", {
                                  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
                                })
                              : "—"}
                            {unit.window_is_custom ? (
                              <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 border-orange-400 text-orange-700" data-testid={`unit-window-source-${unit.unit_id}`}>
                                Ventana personalizada
                              </Badge>
                            ) : (unit.window_source === "line" && (
                              <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 border-amber-400 text-amber-700" data-testid={`unit-window-source-${unit.unit_id}`}>
                                Personalizada
                              </Badge>
                            ))}
                          </div>
                          {/* Assignment info */}
                          <div className="pt-2 border-t space-y-1 text-sm">
                            {vehicle && (
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-green-600" />
                                <span>{formatVehicleLabel(vehicle)}</span>
                              </div>
                            )}
                            {driver && (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-green-600" />
                                <span>{driver.name}</span>
                              </div>
                            )}
                          </div>
                          {/* Folio */}
                          <div className="text-xs text-slate-400">
                            Contrato: {unit.contract_folio}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Assignment Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  Asignar Servicio
                </DialogTitle>
              </DialogHeader>
              {selectedContract && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2" data-testid="assign-dialog-info">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">{selectedContract.client_name}</p>
                      {selectedContract.unit_label && (
                        <Badge variant="outline" className="text-xs" data-testid="assign-dialog-unit-label">{selectedContract.unit_label}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      {selectedContract.origin} → {selectedContract.destination}
                    </p>
                    <p className="text-sm text-slate-500" data-testid="assign-dialog-window">
                      {selectedContract.effective_start ? new Date(selectedContract.effective_start).toLocaleString("es-MX") : "—"}
                      {" → "}
                      {selectedContract.effective_end ? new Date(selectedContract.effective_end).toLocaleString("es-MX") : "—"}
                    </p>
                    {selectedContract.window_source && (
                      <Badge variant="outline" className={cn(
                        "text-[10px]",
                        selectedContract.window_source === "v2" && "border-emerald-400 text-emerald-700",
                        selectedContract.window_source === "line" && "border-amber-400 text-amber-700",
                        selectedContract.window_source === "global" && "border-slate-300 text-slate-600"
                      )} data-testid="assign-dialog-source">
                        {selectedContract.window_source === "v2" ? "Ventana v2" : selectedContract.window_source === "line" ? "Personalizada" : "Hereda contrato"}
                      </Badge>
                    )}
                  </div>

                  {isSelectedPostService && (
                    <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="post-service-banner">
                      <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Modificación post-servicio
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Este servicio ya finalizó. La OS se regenerará automáticamente y queda registrada en auditoría.
                      </p>
                      <div className="mt-2 space-y-1">
                        <label className="text-xs font-medium text-amber-900">
                          Motivo del cambio (mín. 10 caracteres) *
                        </label>
                        <textarea
                          value={postServiceReason}
                          onChange={(e) => setPostServiceReason(e.target.value)}
                          rows={2}
                          className="w-full text-sm rounded-md border border-amber-300 bg-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
                          placeholder="Ej: Corrección operativa solicitada por logística..."
                          data-testid="post-service-reason-input"
                          required
                        />
                        <p className="text-[11px] text-amber-700">{postServiceReason.trim().length} / 10 caracteres mínimos</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      Unidad
                    </label>
                    <Select
                      value={assignmentData.vehicle_id}
                      onValueChange={handleVehicleChange}
                    >
                      <SelectTrigger data-testid="assign-vehicle-select">
                        <SelectValue placeholder="Seleccionar unidad..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getVehiclesForForm(assignmentData.vehicle_id).map(v => {
                          const blocked = isVehicleBlocked(v.id);
                          return (
                            <SelectItem 
                              key={v.id} 
                              value={v.id}
                              disabled={blocked}
                              className={blocked ? "opacity-50" : ""}
                            >
                              <div className="flex items-center gap-2 w-full">
                                {blocked ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center gap-2">
                                        <Ban className="w-4 h-4 text-orange-500" />
                                        <span className="text-slate-400 line-through">
                                          {vehicleSelectLabel(v)}
                                        </span>
                                        <Badge variant="outline" className="bg-orange-50 text-orange-700 text-xs ml-2">
                                          Bloqueado
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Unidad bloqueada por mantenimiento</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>{vehicleSelectLabel(v)} ({v.capacity} pax)</span>
                                  </>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    
                    {assignmentData.vehicle_id && vehicleAvailability[assignmentData.vehicle_id] && !vehicleAvailability[assignmentData.vehicle_id].available && (
                      <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700 flex items-start gap-2" data-testid="vehicle-conflict-warning">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Unidad con conflicto de horario</strong>
                          {vehicleAvailability[assignmentData.vehicle_id].conflicts?.map((c, i) => (
                            <div key={i} className="text-xs mt-1">
                              {c.type === "MAINTENANCE"
                                ? `Mantenimiento: ${c.reason} (${c.start_date} — ${c.end_date})`
                                : `Contrato ${c.contract_folio} — ${c.client_name}`
                              }
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Chofer</label>
                    <Select
                      value={assignmentData.driver_id}
                      onValueChange={handleDriverChange}
                    >
                      <SelectTrigger data-testid="assign-driver-select">
                        <SelectValue placeholder="Seleccionar chofer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getDriversForForm(assignmentData.driver_id).map(d => {
                          const blocked = isDriverBlocked(d.id);
                          return (
                            <SelectItem 
                              key={d.id} 
                              value={d.id}
                              disabled={blocked}
                              className={blocked ? "opacity-50" : ""}
                            >
                              <div className="flex items-center gap-2">
                                {blocked ? (
                                  <>
                                    <Ban className="w-4 h-4 text-orange-500" />
                                    <span className="text-slate-400 line-through">
                                      {driverSelectLabel(d)}
                                    </span>
                                    <Badge variant="outline" className="bg-orange-50 text-orange-700 text-xs ml-2">
                                      Ocupado
                                    </Badge>
                                  </>
                                ) : (
                                  <>
                                    <User className="w-4 h-4" />
                                    {driverSelectLabel(d)}
                                  </>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    
                    {assignmentData.driver_id && driverConflicts[assignmentData.driver_id] && !driverConflicts[assignmentData.driver_id].available && (
                      <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700 flex items-start gap-2" data-testid="driver-conflict-warning">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>Chofer con conflicto de horario</strong>
                          {driverConflicts[assignmentData.driver_id].conflicts?.map((c, i) => (
                            <div key={i} className="text-xs mt-1">
                              Contrato {c.contract_folio} — {c.client_name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Semáforo manual — persiste a nivel contrato (contract.traffic_light).
                      La asignación en sí se guarda vía v2 con ventana efectiva. */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Semáforo</label>
                    <Select
                      value={assignmentData.traffic_light}
                      onValueChange={(value) => setAssignmentData(prev => ({ ...prev, traffic_light: value }))}
                    >
                      <SelectTrigger data-testid="assign-traffic-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rojo">🔴 Rojo - Sin asignación completa</SelectItem>
                        <SelectItem value="amarillo">🟡 Amarillo - Asignación inicial</SelectItem>
                        <SelectItem value="verde">🟢 Verde - Confirmado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Link to={`/contratos/${selectedContract.id}`}>
                      <Button type="button" variant="ghost">Ver Detalle</Button>
                    </Link>
                    <Button 
                      type="submit" 
                      data-testid="save-assignment-btn"
                      disabled={
                        (assignmentData.vehicle_id && isVehicleBlocked(assignmentData.vehicle_id)) ||
                        (assignmentData.driver_id && isDriverBlocked(assignmentData.driver_id))
                      }
                    >
                      Guardar
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </TooltipProvider>
    </Layout>
  );
}
